<template>
  <div class="page-content">
    <!-- Until this page's own load has picked the view: the account an earlier page left in the store
         can lack the company just signed up, or its consent, and would mount the wrong dashboard. -->
    <template v-if="!loaded">
      <LoadingIndicatorComponent />
    </template>
    <!-- Portals may replace the company dashboard entirely (e.g. the
         ecolabel portal shows team management only). Without a selected
         company this falls through to the standard branch, which keeps the
         admin company-management fallback reachable. -->
    <template v-else-if="portal.CompanyDashboard && selectedCompanyOrFirst">
      <component
        :is="portal.CompanyDashboard"
        :company="selectedCompanyOrFirst"
        :key="`portal-${selectedCompanyOrFirst.id}`"
      />
    </template>
    <!-- A company that owes its plan sees sample figures behind the billing prompt. -->
    <template v-else-if="companyAwaitingPlan">
      <PaymentWallComponent
        :company="companyAwaitingPlan"
        :can-leave="hasCompanyInUse(accountData.companies)"
        :key="`payment-wall-${companyAwaitingPlan.id}`"
      />
    </template>
    <template v-else-if="consolidated && allCompaniesBuyers">
      <ConsolidatedBuyerDashboard />
    </template>
    <template v-else-if="consolidated">
      <ConsolidatedDashboardComponent />
    </template>
    <template v-else>
      <!-- Past the load gate the account is this page's answer, which always carries its id. -->
      <template v-if="selectedCompanyOrFirst">
        <TraderDashboard
          v-if="isTraderView"
          :company="selectedCompanyOrFirst"
          :is-admin="isAdminSession"
          :key="`trader-${selectedCompanyOrFirst.id}`"
        />
        <CorporateDashboard
          v-else-if="isCorporateView"
          :company="selectedCompanyOrFirst"
          :is-admin="isAdminSession"
          :key="`buyer-${selectedCompanyOrFirst.id}`"
        />
        <IncomeEstimateView
          v-else-if="showIncomeEstimate"
          :company-name="selectedCompanyOrFirst.name"
          :registry-code="selectedCompanyOrFirst.registry_code"
          :key="`income-${selectedCompanyOrFirst.id}`"
        />
        <BigProducerDashboard
          v-else-if="hasLargeDevicePower"
          :company="selectedCompanyOrFirst"
          :account_id="accountData.id!"
          :is_admin="isAdminSession"
          :show_income_estimate="incomeEstimateEnabled"
          :key="`big-${selectedCompanyOrFirst.id}`"
        />
        <SmallProducerDashboard
          v-else
          :company="selectedCompanyOrFirst"
          :account_id="accountData.id!"
          :is_admin="isAdminSession"
          :showReferral="showReferral"
          :key="`regular-${selectedCompanyOrFirst.id}`"
        />
      </template>
      <!-- AccountDataDebugComponent for debugging -->
      <AccountDataDebugComponent :accountData="accountData" class="mb-4" />
      <!-- Admin only content -->
      <AdminCompanyManagementComponent
        v-if="accountData.admin_account_id"
        :accountData="accountData"
        :companyIndex="companyIndexOrFirst"
        :key="companyIndexOrFirst"
      />
      <!-- Admin only content end -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { companyAwaitingPayment, hasCompanyInUse } from "@/billing/paymentWall";
import PaymentWallComponent from "@/billing/PaymentWallComponent.vue";
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useAccount } from "@/composables/account/useAccount";
import { IS_PRODUCER_VIEW } from "@/constants";
import { useAdminSession } from "@/composables/useAdminSession";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useConsolidated } from "@/composables/useConsolidated";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import AdminCompanyManagementComponent from "@/dashboard/admin/AdminCompanyManagementComponent.vue";
import BigProducerDashboard from "@/dashboard/BigProducerDashboard.vue";
import { getPortal } from "@/portals";
import ConsolidatedBuyerDashboard from "@/dashboard/buyer/ConsolidatedBuyerDashboard.vue";
import CorporateDashboard from "@/dashboard/buyer/CorporateDashboard.vue";
import ConsolidatedDashboardComponent from "@/dashboard/consolidated/ConsolidatedDashboardComponent.vue";
import IncomeEstimateView from "@/dashboard/income_estimate/IncomeEstimateView.vue";
import SmallProducerDashboard from "@/dashboard/small_producer/SmallProducerDashboard.vue";
import TraderDashboard from "@/dashboard/trader/TraderDashboard.vue";
import type { Account } from "@/types";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

const portal = getPortal();
const route = useRoute();
const router = useRouter();

const afterFetchAccount = async (accountData: Account) => {
  if (accountData.companies.length === 0 && !accountData.admin_account_id) {
    router.push("/signup");
    return;
  }

  // Set query params if neither company_id nor consolidated are set
  const hasCompanyIdParam =
    route.query.company_id !== undefined || route.query.companyId !== undefined;
  const hasConsolidatedParam = route.query.consolidated !== undefined;

  if (!hasCompanyIdParam && !hasConsolidatedParam) {
    const hasMultipleCompanies = accountData.companies.length > 1;
    const firstCompanyId = accountData.companies[0]?.id;

    // Portals without a consolidated view (the router guard bounces it
    // away) always land on the first company instead.
    if (!portal.hasConsolidatedView && firstCompanyId) {
      await router.replace({
        path: "/dashboard",
        query: {
          company_id: firstCompanyId.toString(),
          consolidated: "false",
        },
      });
    } else if (hasMultipleCompanies) {
      await router.replace({
        path: "/dashboard",
        query: { consolidated: "true" },
      });
    } else if (firstCompanyId) {
      await router.replace({
        path: "/dashboard",
        query: {
          company_id: firstCompanyId.toString(),
          consolidated: "false",
        },
      });
    }
  }
};

const { accountData, loaded } = useAccount(afterFetchAccount);

const isAdminSession = useAdminSession(() => accountData);

const { consolidated } = useConsolidated();

const { selectedCompanyOrFirst, companyIndexOrFirst } =
  useCompanyFinder(accountData);

const companyAwaitingPlan = computed(() =>
  companyAwaitingPayment(
    accountData.companies,
    consolidated.value ? null : selectedCompanyOrFirst.value?.id
  )
);

const { hasFlag } = useFeatureFlags(selectedCompanyOrFirst);
const incomeEstimateEnabled = hasFlag("income_estimate_enabled");

const dashboardView = computed(
  () => selectedCompanyOrFirst.value?.dashboard_view
);
const isCorporateView = computed(() => dashboardView.value === "corporate");
const isTraderView = computed(() => dashboardView.value === "trader");

// Traders count too: most carried the corporate flag as well before dashboard_view, and got this view.
const allCompaniesBuyers = computed(
  () =>
    accountData.companies.length > 0 &&
    accountData.companies.every(
      (company) => !IS_PRODUCER_VIEW[company.dashboard_view]
    )
);

// kW across the account's devices; above 1 MW a default-view producer gets the big-producer dashboard.
const devicePower = computed(() => accountData.total_devices_power ?? 0);

const hasLargeDevicePower = computed(
  () => dashboardView.value === "big_producer" || devicePower.value > 1000
);

const showIncomeEstimate = computed(() => {
  if (!incomeEstimateEnabled.value) return false;
  // route.query.view can be string | null | (string | null)[] when the same
  // key appears more than once in the URL — collapse to the first value.
  const raw = route.query.view;
  const view = Array.isArray(raw) ? raw[0] : raw;
  return view === "income-estimate";
});

const showReferral = computed(
  () => devicePower.value > 0 && devicePower.value <= 1000
);
</script>
